// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {filter, isEmpty, isNumber, get, map, orderBy, startCase} from 'lodash';

// Components
import {Dropdown} from 'primereact/dropdown';
import Button from '../core/button';
import MappingButton from '../app/mapping-button';
import MoviesAndProgramsSearchForm from './movies-and-programs-search-form';
import SearchPagination from '../app/search-pagination';
import SearchTableWithSort from '../app/search-tablewithsort';
import ServicesExportPanelAction from '../app/services-export-panel-action';
import ServicesSearchPanelAction from '../app/services-search-panel-action';
import Thumbnail from '../core/thumbnail';

// Containers
import withSearch from '../../containers/with-search';

// Styles
import './movies-and-programs-mapping.scss';
import {DrawLegend, mappingColors} from '../core/colorizeRows';
import rsqlConfig from '../../../config/rsql-config';
import filterConfig from '../../../config/filter-config';

const servicesDataKey = 'moviesAndProgramsMappingFranchise';

class MoviesAndProgramsMappingFranchise extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showSearchPanel: false,
            filters: filterConfig.moviesAndProgramsMapping,
            selectedFilter: null,
        };
    }

    componentDidMount() {
        if (this.getMediaItemGuid()) {
            this.searchByMediaItemShow();
        }
    }

    componentDidUpdate(prevProps) {
      const { colorFilterSet } = this.props;

      // If the color filter for this service has changed,
      // update the local state with the new value.
      if (colorFilterSet && colorFilterSet[servicesDataKey] !==
        prevProps.colorFilterSet?.[servicesDataKey]
      ) {
        this.setState({
          selectedFilter: colorFilterSet[servicesDataKey],
        });
      } else if (colorFilterSet && colorFilterSet[servicesDataKey] === null &&
        prevProps.colorFilterSet?.[servicesDataKey] !== null
      ) {
        this.setState({
          selectedFilter: null,
        });
      }

      // If the media item GUID has changed, perform the search update.
      if (this.getMediaItemGuid(prevProps) !== this.getMediaItemGuid(this.props)) {
        this.searchByMediaItemShow(this.props);
      }
    }

    onFilterChange(e) {
        const {code} = e.value;
        const {servicesDataSearch, setColorFilter, setFieldValue} = this.props;
        this.setState({selectedFilter: code !== 'linkedNot' ? e.value : null});

        setColorFilter(servicesDataKey, code !== 'linkedNot' ? e.value : null);
        setFieldValue(servicesDataKey, 'linkedOnlyFilter', '');
        servicesDataSearch(servicesDataKey);
        if (code !== 'linkedNot') {
            setFieldValue(servicesDataKey, 'linkedOnlyFilter', `=rsql=${rsqlConfig[code]}`);
            servicesDataSearch(servicesDataKey);
        }
    }

    getPaginationProps() {
        const {query, totalPages} = this.props;

        return {
            maxResults: 10000,
            maxPages: 9,
            query,
            totalPages,
        };
    }

    getMediaItemGuid(props = this.props) {
        return get(props.mediaItemData, 'guid');
    }

    getTableWithSortProps() {
        const {getMappingButtonProps, renderDetail, renderThumbnails} = this.props;

        return {
            className: 'movies-and-programs-mapping--details',
            columnConfig: [
                {
                    dataKey: 'title',
                    formatter: 'moviesAndProgramsTitle',
                    sortKey: 'title.sortable',
                    title: 'Title',
                    width: '17%',
                },
                {
                    dataKey: 'episodeSeason',
                    title: 'S',
                    tooltip: 'Episode Season',
                    width: '5%',
                },
                {
                    dataKey: 'episodeNumber',
                    title: 'E',
                    tooltip: 'Episode Number',
                    width: '5%',
                },
                {
                    dataKey: 'echostarContentType',
                    title: 'Type',
                    tooltip: 'Echostar Content Type',
                    width: '6%',
                },
                {
                    dataKey: 'description',
                    title: 'Description',
                    width: '36%',
                },
                {
                    dataKey: 'releaseYear',
                    title: 'Released',
                    tooltip: 'Release Year',
                    type: 'string',
                    width: '7%',
                },
                {
                    dataKey: 'originalAirDate',
                    title: 'Original Air',
                    tooltip: 'Original Air Date',
                    type: 'date',
                    width: '12%',
                },
                {
                    dataKey: '',
                    title: '',
                    type: 'component',
                    component: MappingButton,
                    componentProps: (searchData) => ({
                        ...getMappingButtonProps(searchData),
                        servicesDataKey,
                    }),
                    width: '12%',
                },
            ],
            rowDetails: {
                blacklistKeys: ['flaggedMetadata'],
                headerRenderer: renderThumbnails,
                renderDetail,
                isMapping: true,
            },
            onReset: this.searchByMediaItemShow,
            mediaItemData: {},
            ...this.props,
        };
    }

    searchByMediaItemShow = (props = this.props) => {
        const {mediaItemData = {}, resetServiceData, servicesDataSearch, setFieldValues} = props;
        const searchTitleArray = mediaItemData.showName ? mediaItemData.showName.split('_') : [];
        const searchTitle =
            searchTitleArray.length === 2 && isNumber(searchTitleArray[1])
                ? searchTitleArray[0]
                : searchTitleArray.join(' ');

        if (this.getMediaItemGuid(props)) {
            resetServiceData(false).then(() => {
                setFieldValues(
                    servicesDataKey,
                    {
                        'title||seriesTitle': searchTitle,
                    },
                    false
                ).then(() => {
                    servicesDataSearch(servicesDataKey);
                });
            });
        }
    };

    searchByMediaItemData = (props = this.props, searchField) => {
        const {mediaItemData = {}, setFieldValues, setQuery, refreshServicesData} = props;

        if (searchField) {
            Promise.all([
                setFieldValues(servicesDataKey, {
                    'title||seriesTitle': get(mediaItemData, searchField, ''),
                }),
                setQuery(servicesDataKey, {
                    page: 1,
                }),
            ]).then(() => {
                refreshServicesData(servicesDataKey);
            });
        }
    };

    renderSearchButton(fieldName, disabled = false) {
        const {mediaItemData = {}} = this.props;
        const props = {
            disabled,
            small: true,
            className: 'movies-and-programs-mapping--search-button',
            onClick: () => {
                this.setState({showSearchPanel: true});
                this.searchByMediaItemData(this.props, fieldName);
            },
        };
        const shouldRender = get(mediaItemData, fieldName);

        return (shouldRender ? (
            <Button {...{
                mediaItemData: {},
                ...props,
                }}
            >{startCase(fieldName)}
            </Button>
            ) : null);
    }

    renderForm = () => <MoviesAndProgramsSearchForm formName={servicesDataKey} servicesDataKey={servicesDataKey} />;

    renderThumbnails = (searchData) => {
        const images = orderBy(filter(get(searchData, '0.images', []), 'url'), 'category');

        return images.length ? (
            <div className="movies-and-programs-mapping--thumbnail-group">
                {map(images, ({amsGalleryId, category, height, url, width}) => (
                    <Thumbnail
                        className="movies-and-programs-mapping--thumbnail"
                        height={height}
                        key={amsGalleryId}
                        label={startCase(category)}
                        src={url}
                        width={width}
                    />
                ))}
            </div>
        ) : null;
    };

    renderFilters = () => {
        const {className: classNameProp} = this.props;
        const {filters, selectedFilter} = this.state;
        return (
            <div className={`${classNameProp}-filters`}>
                <Dropdown
                    optionLabel="name"
                    options={filters}
                    placeholder="Select a Filter"
                    value={selectedFilter}
                    onChange={(e) => this.onFilterChange(e)}
                />
            </div>
        );
    };

    render() {
        const {className: classNameProp, mediaItemData = {}, searchData} = this.props;
        const {selectedFilter} = this.state;
        const className = classnames('movies-and-programs-mapping-show', classNameProp);
        const {showSearchPanel} = this.state;

        return !isEmpty(mediaItemData) ? (
            <div className={className}>
                <div className="movies-and-programs-mapping--search-buttons">
                    {this.renderSearchButton('title', selectedFilter)}
                    {this.renderSearchButton('showName', selectedFilter)}
                    {this.renderSearchButton('episodeName', selectedFilter)}
                </div>
                <ServicesExportPanelAction exportConfig="moviesAndPrograms" searchData={searchData} />
                <ServicesSearchPanelAction contentRenderer={this.renderForm} showPanel={showSearchPanel} />
                <div style={{display: 'flex'}}>
                    <SearchPagination {...this.getPaginationProps()} />
                    {this.renderFilters()}
                    <DrawLegend className="movies-and-programs-mapping--legend" data={mappingColors} />
                </div>
                <SearchTableWithSort {...this.getTableWithSortProps()} />
            </div>
        ) : null;
    }
}

MoviesAndProgramsMappingFranchise.propTypes = {
    className: PropTypes.string,
    colorFilterSet: PropTypes.object,
    filterByEpisodes: PropTypes.bool,
    getMappingButtonProps: PropTypes.func.isRequired,
    mediaItemData: PropTypes.object,
    query: PropTypes.object,
    refreshServicesData: PropTypes.func.isRequired,
    renderDetail: PropTypes.func.isRequired,
    renderThumbnails: PropTypes.func.isRequired,
    resetServiceData: PropTypes.func.isRequired,
    searchData: PropTypes.array,
    servicesDataSearch: PropTypes.func.isRequired,
    servicesSearchData: PropTypes.object,
    setColorFilter: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    setFieldValues: PropTypes.func.isRequired,
    setQuery: PropTypes.func.isRequired,
    totalPages: PropTypes.number,
};

export default withSearch(MoviesAndProgramsMappingFranchise, servicesDataKey);
